// How the SPA reads a per-tenant setting.
//
// The registry lives on the server (api/app/Services/tenantSettings.js) and
// `GET /api/organization` already returns the tenant's resolved values under
// `settings` — every key the registry declares, always, with stored values
// layered over the registry's defaults. main.js dispatches fetchOrganization
// WITHOUT awaiting it, and swallows a failure with `.catch(() => {})`, so
// `state.organization` being null is a real, reachable state and not merely
// the instant before the response lands. Every reader here therefore has to
// answer something usable with no organization at all.
//
// WHY A FALLBACK ARGUMENT RATHER THAN THE REGISTRY'S DEFAULT. This module
// cannot see the registry: the schema only arrives on the Preferences page's
// own request, and a page that draws a map or a form must not wait for it. So
// each caller names the value it would have used before this setting existed,
// which keeps the pre-settings behaviour as the thing that happens while the
// org is in flight, instead of a second copy of the server's default drifting
// quietly out of step.
//
// A stored value whose type no longer matches the registry is already
// resolved back to the default server-side, so this module does no coercion:
// what it guards against is the absence of the organization, not a bad value
// inside one.

/**
 * orgSetting(store, key, fallback) -> the tenant's value for `key`.
 *
 * `store` is the Vuex store (or `this.$store`). Defensive about every level of
 * the path in the same idiom the rest of the app uses ($store && $store.getters
 * && ...): several suites mock `$store` as a bare object with no `state` at
 * all, and a settings read must not be the thing that throws in them.
 */
export function orgSetting(store, key, fallback) {
  const org = store && store.state && store.state.organization;
  const settings = org && org.settings;
  if (!settings) return fallback;
  const value = settings[key];
  return value === undefined || value === null ? fallback : value;
}
